import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clubAPI, authAPI } from '../services/api';
import './BookieCallback.css';

function BookieCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Connecting your bookie account...');
  const [countdown, setCountdown] = useState(5);
  const [user, setUser] = useState(null);

  // Load user data on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await authAPI.getCurrentUser();
        setUser(userData.user);
      } catch (err) {
        console.error('Failed to load user:', err);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (!user) return; // Wait for user data to load

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Simulate bookie connection after 5 seconds
    const connectionTimer = setTimeout(async () => {
      setMessage('Finalizing connection...');

      try {
        // Send dummy callback data to backend using real user info
        const clubId = searchParams.get('clubId');

        // Generate random Australian address data
        const ausStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
        const ausStreets = [
          'Collins Street', 'Bourke Street', 'George Street', 'Pitt Street',
          'Elizabeth Street', 'King Street', 'Queen Street', 'Albert Street',
          'Edward Street', 'William Street', 'Adelaide Street', 'Mary Street',
          'Margaret Street', 'Ann Street', 'Charlotte Street', 'Victoria Street'
        ];
        const ausCities = {
          'NSW': ['Sydney', 'Newcastle', 'Wollongong', 'Parramatta', 'Penrith'],
          'VIC': ['Melbourne', 'Geelong', 'Ballarat', 'Bendigo', 'Frankston'],
          'QLD': ['Brisbane', 'Gold Coast', 'Townsville', 'Cairns', 'Toowoomba'],
          'SA': ['Adelaide', 'Mount Gambier', 'Whyalla', 'Murray Bridge'],
          'WA': ['Perth', 'Fremantle', 'Bunbury', 'Kalgoorlie', 'Mandurah'],
          'TAS': ['Hobart', 'Launceston', 'Burnie', 'Devonport'],
          'NT': ['Darwin', 'Alice Springs', 'Palmerston'],
          'ACT': ['Canberra', 'Belconnen', 'Tuggeranong', 'Gungahlin']
        };

        // Pick random state and city
        const randomState = ausStates[Math.floor(Math.random() * ausStates.length)];
        const citiesForState = ausCities[randomState];
        const randomCity = citiesForState[Math.floor(Math.random() * citiesForState.length)];
        const randomStreetNumber = Math.floor(Math.random() * 200) + 1;
        const randomStreet = ausStreets[Math.floor(Math.random() * ausStreets.length)];

        // Generate Australian mobile number (04XX XXX XXX)
        const generateAusMobile = () => {
          const prefix = '04';
          const secondPart = Math.floor(Math.random() * 90) + 10; // 10-99
          const thirdPart = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
          const fourthPart = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
          return `${prefix}${secondPart} ${thirdPart} ${fourthPart}`;
        };

        const dummyCallbackData = {
          clubId: clubId,
          userId: user.id,
          bookieCustomerId: 'BOOKIE_' + user.id + '_' + Math.random().toString(36).substring(7).toUpperCase(),
          bookieUsername: user.email.split('@')[0] + '_bookie',
          bookieEmail: user.email,
          first_name: user.firstname,
          last_name: user.surname || 'Surname',
          street: `${randomStreetNumber} ${randomStreet}`,
          city: randomCity,
          state: randomState,
          country: 'Australia',
          phone: generateAusMobile(),
          status: 'ACTIVE',
          clubBettingWalletId: 'WALLET_' + clubId + '_BETTING',
          clubKittyWalletId: 'KITTY_' + clubId + '_KITTY',
          verificationStatus: 'VERIFIED',
          timestamp: new Date().toISOString()
        };

        // Call the callback endpoint
        await clubAPI.bookieCallback(dummyCallbackData);

        setStatus('success');
        setMessage('Successfully connected your bookie account!');

        // Redirect: if signup flow, go to invite step; otherwise dashboard
        const isSignup = searchParams.get('signup') === '1';
        const redirectClubId = searchParams.get('clubId');
        const target = (isSignup && redirectClubId)
          ? `/signup/invite?clubId=${redirectClubId}`
          : '/dashboard';

        setTimeout(() => {
          navigate(target);
        }, 2000);

      } catch (error) {
        console.error('Callback error:', error);
        setStatus('error');
        setMessage('Connection failed. Please try again.');

        // Redirect to dashboard after 5 seconds even on error
        setTimeout(() => {
          navigate('/dashboard');
        }, 5000);
      }
    }, 5000); // 5 seconds

    return () => {
      clearInterval(timer);
      clearTimeout(connectionTimer);
    };
  }, [navigate, searchParams, user]);

  return (
    <div className="bookie-callback-wrapper">
      <div className="bookie-callback-container">
        <div className="callback-logo">
          <img src="/logo.png" alt="Punt Club Logo" />
        </div>

        <div className={`callback-status callback-status-${status}`}>
          {status === 'processing' && (
            <>
              <div className="spinner"></div>
              <h2>Connecting Bookie Account</h2>
              <p>{message}</p>
              <div className="countdown">
                <div className="countdown-circle">
                  <svg viewBox="0 0 100 100">
                    <circle
                      className="countdown-bg"
                      cx="50"
                      cy="50"
                      r="45"
                    />
                    <circle
                      className="countdown-progress"
                      cx="50"
                      cy="50"
                      r="45"
                      style={{
                        strokeDashoffset: `${(countdown / 30) * 283}px`
                      }}
                    />
                  </svg>
                  <div className="countdown-number">{countdown}s</div>
                </div>
                <p className="countdown-text">
                  This is a demo mode. Simulating bookie connection...
                </p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="success-icon">✓</div>
              <h2>Connection Successful!</h2>
              <p>{message}</p>
              <p className="redirect-text">Redirecting to dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="error-icon">✗</div>
              <h2>Connection Failed</h2>
              <p>{message}</p>
              <p className="redirect-text">Redirecting to dashboard...</p>
            </>
          )}
        </div>

        <div className="callback-info">
          <p>
            <strong>Demo Mode:</strong> This simulates the return from the bookie after account setup.
            In production, this would be triggered by the bookie's redirect.
          </p>
        </div>
      </div>
    </div>
  );
}

export default BookieCallback;
